import type {
  RenderCaption,
  RenderClip,
  RenderJob,
  RenderSource,
  RenderTimeline,
  RenderTrack
} from '../../../../core/render-runtime/worker/render-job.model';
import type { RenderOutputSettings } from '../../../../core/render-runtime';
import type { ReviewAssetSequenceRenderAsset } from '../review-image-rendering.models';
import { resolveAssetSequenceDurationSeconds, isAssetSequenceVideoSource } from '../asset-sequence-timing.util';

/**
 * assets-render-job.adapter — pure mapping from the "Assets-use" render inputs
 * (the options `ReviewImageRenderingService.renderAssetSequenceToVideo(...)` and
 * `.render(...)` build) to the unified, serializable `RenderJob` contract the
 * background render worker consumes.
 *
 * It is a PURE function: no DOM, no decode, no I/O. Media is carried as a URL on
 * `RenderSource`; the worker probes real durations/sizes via mediabunny.
 *
 * ---------------------------------------------------------------------------
 * MAPPING OVERVIEW
 * ---------------------------------------------------------------------------
 * SEQUENCE (renderAssetSequenceToVideo)
 *   Each selected asset is shown for its own slot duration, BACK-TO-BACK on the
 *   output timeline. So:
 *     - Each scene's visual (image OR video) → one `RenderSource` (deduped by url)
 *       + one `RenderClip` on the base VIDEO track occupying the sequential window
 *       `[cursor, cursor + slotDuration)`. A video scene reads its source from 0
 *       (looping/clamping is the worker's job, mirroring the legacy seek logic);
 *       an image scene carries no source cut window.
 *     - The scene's own `audioUrl` (also the audio track of a video asset, when
 *       present) → an `audio` RenderClip on the audio track, same window, volume 1.
 *     - The scene's baked Piper `narrationUrl` → a SEPARATE `audio` RenderClip,
 *       same window, carrying `narrationPlaybackRate`. This mirrors
 *       `buildSequenceSoundtracks` exactly (asset own-audio + narration are two
 *       independent audio layers; narration never drives scene advance).
 *     - When subtitles are on, each scene's `description` (else the render-wide
 *       `description`) → a `RenderCaption` spanning that scene's window, anchored
 *       low (mirrors the legacy bottom-fade subtitle band).
 *
 * SINGLE IMAGE (render)
 *   A degenerate one-scene sequence: a single image RenderSource + clip spanning
 *   the whole duration, with the title/caption as a render-wide caption. Built by
 *   `buildSingleImageRenderJob`, which forwards to `buildAssetSequenceRenderJob`.
 *
 * FIT / FRAMING
 *   `fitMode:'contain'` → letterbox on black (worker fit 'contain', background
 *   '#000'); `fitMode:'cover'` (default) → fill+crop (worker fit 'cover'). The
 *   legacy still-image Ken-Burns zoom/pan is a preview nicety; the deterministic
 *   worker render places the still full-frame (the legacy frame-encode path does
 *   not animate either), so no per-scene transform animation is emitted.
 *
 * OUTPUT
 *   width/height from aspectRatio (9:16 = 1080×1920, 16:9 = 1920×1080). fps /
 *   bitrate from `outputSettings` when present, else sane defaults. One cover
 *   thumbnail (the legacy asset render emits a single cover frame).
 *
 * ---------------------------------------------------------------------------
 * ASSUMPTIONS (documented in the task report)
 * ---------------------------------------------------------------------------
 * - Slot durations are resolved the SAME way the legacy renderer resolves them
 *   (`resolveAssetSequenceDurationSeconds`): a known/declared duration wins, else
 *   5s for video / 10s for image. The caller may also pre-stamp `durationSeconds`
 *   on each asset (the Smart Video flow does), which is honored as the declared
 *   value. A caller-supplied `maxSlotSeconds` clamps each slot.
 * - Video scenes are read from source time 0 with no explicit end; the worker
 *   loops a short clip / clamps a long one to the slot, mirroring the legacy
 *   `seekSequenceVideoFrame` mapping. Image scenes drop the source cut window.
 * - The scene own-audio + narration audio are carried as their natural full
 *   length starting at the scene offset; the worker trims to the clip window.
 * - `RenderCaption` style is best-effort (low-anchored, centered); the worker
 *   compositor owns exact glyph rendering.
 */

export interface AssetsSequenceRenderJobArgs {
  /** Stable job id; a random id is generated when omitted. */
  id?: string | null;

  /** Selected scene assets (image/video), in display order. Must have directUrl. */
  assets: ReviewAssetSequenceRenderAsset[];

  /** 9:16 portrait (default) vs 16:9 landscape. Drives output dimensions. */
  aspectRatio?: '9:16' | '16:9' | null;

  /** 'cover' (fill+crop, default) | 'contain' (letterbox on black). */
  fitMode?: 'cover' | 'contain' | null;

  /** Burn the per-scene description as a subtitle caption. */
  includeSubtitles?: boolean | null;

  /** Render-wide fallback subtitle text when a scene has no description. */
  description?: string | null;

  /** Skip the optional per-scene own-audio + narration audio layers. */
  skipOptionalAudio?: boolean | null;

  /** Per-slot duration cap (seconds). Matches `assetSequenceRenderMaxSeconds()`. */
  maxSlotSeconds?: number | null;

  /** Output overrides resolved on the main thread by RenderOutputPolicyService. */
  outputSettings?: RenderOutputSettings | null;

  /** Cover thumbnails to capture (1..15, default 1). Default reproduces the single cover frame. */
  thumbnailCount?: number | null;
}

export interface SingleImageRenderJobArgs {
  /** Stable job id; a random id is generated when omitted. */
  id?: string | null;

  /** The single image to render into a video. */
  imageUrl: string;

  /** Whole-render duration (seconds). */
  durationSeconds: number;

  /** Optional title / caption burned as a render-wide caption. */
  title?: string | null;
  caption?: string | null;

  /** 9:16 portrait (default) vs 16:9 landscape. */
  aspectRatio?: '9:16' | '16:9' | null;

  /** Output overrides. */
  outputSettings?: RenderOutputSettings | null;

  /** Cover thumbnails to capture (1..15, default 1). */
  thumbnailCount?: number | null;
}

const DEFAULT_FPS = 30;
const DEFAULT_BITRATE = 8_000_000;
const EPSILON = 0.05;

/**
 * buildAssetSequenceRenderJob — maps the asset-sequence render inputs to a
 * RenderJob. Pure and deterministic (modulo the generated id).
 */
export function buildAssetSequenceRenderJob(args: AssetsSequenceRenderJobArgs): RenderJob {
  const assets = (args.assets || []).filter(asset => !!(asset.directUrl || '').trim());
  if (!assets.length) {
    throw new Error('Selected assets do not have playable media URLs.');
  }

  const { width, height } = resolveDimensions(args.aspectRatio);
  const fit: 'cover' | 'contain' = args.fitMode === 'contain' ? 'contain' : 'cover';
  const includeSubtitles = args.includeSubtitles === true;
  const skipAudio = args.skipOptionalAudio === true;
  const maxSlot = positiveOrNull(args.maxSlotSeconds) ?? Number.POSITIVE_INFINITY;

  const sources: RenderSource[] = [];
  const urlToSourceId = new Map<string, string>();

  /** Register (or reuse) a source for a given media url, returning its id. */
  const ensureSource = (url: string | null | undefined, kind: RenderSource['kind']): string | null => {
    const normalized = (url || '').trim();
    if (!normalized) return null;
    const existing = urlToSourceId.get(normalized);
    if (existing) return existing;
    const id = `src-${kind}-${sources.length}`;
    urlToSourceId.set(normalized, id);
    sources.push({ id, kind, blob: null, url: normalized });
    return id;
  };

  const videoTrack: RenderTrack = { id: 'track-video', kind: 'video', clips: [] };
  const audioTrack: RenderTrack = { id: 'track-audio', kind: 'audio', clips: [] };
  const captions: RenderCaption[] = [];

  let cursor = 0;
  assets.forEach((asset, index) => {
    const isVideo = isAssetSequenceVideoSource(asset.kind, asset.directUrl);
    const slot = resolveAssetSequenceDurationSeconds({
      kind: asset.kind,
      directUrl: asset.directUrl ?? null,
      declaredDurationSeconds: asset.durationSeconds ?? null,
      decodedVideoDurationSeconds: null,
      maxDurationSeconds: maxSlot
    });
    const start = cursor;
    const end = cursor + slot;
    cursor = end;

    const visualKind: RenderSource['kind'] = isVideo ? 'video' : 'image';
    const sourceId = ensureSource(asset.directUrl, visualKind);
    if (!sourceId) return; // asset with no usable media — skip (guarded above too).

    const clip: RenderClip = {
      id: `scene-${index}`,
      sourceId,
      kind: isVideo ? 'video' : 'image',
      startSeconds: round3(start),
      endSeconds: round3(end),
      transform: { x: 0, y: 0, width: 1, height: 1, fit },
      // A video scene plays from its start; the worker loops/clamps to the slot
      // (mirrors the legacy per-frame seek). Image scenes carry no cut window.
      ...(isVideo ? { sourceStartSeconds: 0 } : {}),
      // Each scene's own audio rides on its dedicated audio clip below; mute the
      // video clip's inline audio so a video asset is not double-counted.
      ...(isVideo ? { muted: true } : {})
    };
    videoTrack.clips.push(clip);

    if (!skipAudio) {
      // Scene own-audio (also the audio track of a video asset, when present).
      const ownAudioId = ensureSource(asset.audioUrl, 'audio');
      if (ownAudioId) {
        audioTrack.clips.push({
          id: `scene-${index}-audio`,
          sourceId: ownAudioId,
          kind: 'audio',
          startSeconds: round3(start),
          endSeconds: round3(end),
          sourceStartSeconds: 0,
          volume: 1
        });
      }

      // Piper narration baked per scene — same window, with its own rate.
      const narrationId = ensureSource(asset.narrationUrl, 'audio');
      if (narrationId) {
        const rate = safePlaybackRate(asset.narrationPlaybackRate);
        audioTrack.clips.push({
          id: `scene-${index}-narration`,
          sourceId: narrationId,
          kind: 'audio',
          startSeconds: round3(start),
          endSeconds: round3(end),
          sourceStartSeconds: 0,
          sourceEndSeconds: round3(slot * rate),
          playbackRate: rate,
          volume: 1
        });
      }
    }

    if (includeSubtitles) {
      const text = (asset.description || args.description || '').replace(/\s+/g, ' ').trim();
      if (text) {
        captions.push({
          id: `scene-${index}-caption`,
          text,
          startSeconds: round3(start),
          endSeconds: round3(end),
          x: 0.5,
          y: 0.88,
          bold: true,
          align: 'center'
        });
      }
    }
  });

  const durationSeconds = Math.max(EPSILON, cursor);
  const tracks: RenderTrack[] = [videoTrack, audioTrack].filter(track => track.clips.length > 0);

  const timeline: RenderTimeline = {
    durationSeconds: round3(durationSeconds),
    tracks,
    ...(captions.length ? { captions } : {}),
    // Letterbox renders fill the bars with black, matching the legacy 'contain' path.
    ...(fit === 'contain' ? { backgroundColor: '#000000' } : {})
  };

  return {
    id: args.id || generateId(),
    origin: 'assets',
    sources,
    timeline,
    output: {
      width,
      height,
      fps: positiveOr(args.outputSettings?.fps, DEFAULT_FPS),
      bitrate: positiveOr(args.outputSettings?.targetBitrate, DEFAULT_BITRATE),
      videoCodec: 'avc',
      thumbnailCount: clampThumbnailCount(args.thumbnailCount)
    }
  };
}

/**
 * buildSingleImageRenderJob — maps the single-image render (`render(...)`) to a
 * RenderJob. Modeled as a one-scene image sequence with the title/caption as a
 * whole-render caption. This flow has no audio today (matches legacy output).
 */
export function buildSingleImageRenderJob(args: SingleImageRenderJobArgs): RenderJob {
  const imageUrl = (args.imageUrl || '').trim();
  if (!imageUrl) {
    throw new Error('The review image has no source URL.');
  }
  const duration = Math.max(EPSILON, finiteOr(args.durationSeconds, 12));

  const job = buildAssetSequenceRenderJob({
    id: args.id,
    assets: [{ directUrl: imageUrl, kind: 'image', durationSeconds: duration }],
    aspectRatio: args.aspectRatio ?? '9:16',
    fitMode: 'cover',
    includeSubtitles: false,
    skipOptionalAudio: true,
    outputSettings: args.outputSettings ?? null,
    thumbnailCount: args.thumbnailCount ?? null
  });

  const text = [args.title, args.caption]
    .map(value => (value || '').trim())
    .filter(Boolean)
    .join('\n');
  if (text) {
    job.timeline.captions = [{
      id: 'image-caption',
      text,
      startSeconds: 0,
      endSeconds: round3(duration),
      x: 0.5,
      y: 0.82,
      bold: true,
      align: 'center'
    }];
  }
  return job;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** Output dimensions: 16:9 landscape = 1920×1080; everything else (9:16) = 1080×1920. */
function resolveDimensions(aspectRatio: '9:16' | '16:9' | null | undefined): { width: number; height: number } {
  return aspectRatio === '16:9'
    ? { width: 1920, height: 1080 }
    : { width: 1080, height: 1920 };
}

function safePlaybackRate(value: number | null | undefined): number {
  const rate = Number(value);
  return Number.isFinite(rate) && rate > 0
    ? Math.max(0.85, Math.min(1.25, rate))
    : 1;
}

function finiteOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function positiveOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function positiveOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/** Cover-thumbnail count clamped to 1..15 (default 1 for any non-positive input). */
function clampThumbnailCount(count: number | null | undefined): number {
  const n = Math.trunc(Number(count ?? 1));
  return Number.isFinite(n) && n > 0 ? Math.min(15, n) : 1;
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function generateId(): string {
  return globalThis.crypto?.randomUUID?.() || `assets-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
